import type { BreadcrumbItem } from "@core/shared/types";
import { isRTL } from "@core/shared/utils";
import { BaseLink } from "@design-system/components/link";
import { cn } from "@design-system/utils";
import { ChevronLeftIcon, ChevronRightIcon } from "@radix-ui/react-icons";

type BreadcrumbProps = {
  items: BreadcrumbItem[];
  maxItems?: number; // Maximum number of items to show on mobile only
};

export function Breadcrumb({ items, maxItems = 3 }: BreadcrumbProps) {
  const isRTLMode = isRTL();

  // On mobile, show only first, last, and optionally middle items
  const getVisibleItems = () => {
    if (items.length <= maxItems) return items;

    const firstItem = items[0];
    const lastItem = items[items.length - 1];
    const middleItems = items.slice(1, -1);

    // If we have space for one middle item, show it
    if (maxItems >= 3 && middleItems.length > 0) {
      const middleIndex = Math.floor(middleItems.length / 2);
      return [firstItem, middleItems[middleIndex], lastItem];
    }

    return [firstItem, lastItem];
  };

  const visibleItems = getVisibleItems();

  return (
    <nav className="flex capitalize" aria-label="Breadcrumb">
      <ol
        className={cn(
          "flex items-center overflow-hidden",
          isRTLMode ? "space-x-reverse" : "space-x-1 md:space-x-2",
        )}
      >
        {/* Mobile view - limited items */}
        <div className="flex w-full items-center md:hidden">
          {visibleItems.map((item, index) => {
            const isLast = index === visibleItems.length - 1;
            const isFirst = index === 0;
            const isMiddle = !isFirst && !isLast;

            return (
              <li
                key={`mobile-${index}`}
                className="flex min-w-0 flex-shrink items-center"
              >
                {index > 0 && (
                  <>
                    <ChevronLeftIcon
                      className={cn(
                        "h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500 ltr:hidden",
                        isRTLMode ? "mr-1" : "ml-1",
                      )}
                      aria-hidden="true"
                    />
                    <ChevronRightIcon
                      className={cn(
                        "h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500 rtl:hidden",
                        isRTLMode ? "mr-1" : "ml-1",
                      )}
                      aria-hidden="true"
                    />
                  </>
                )}

                {item.url ? (
                  <BaseLink
                    to={item.url}
                    className={cn(
                      "truncate text-xs font-medium",
                      isRTLMode ? "mr-1" : "ml-1",
                      isLast
                        ? "text-gray-700 dark:text-gray-300"
                        : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300",
                      // On mobile, show ellipsis for middle items
                      isMiddle && "max-w-16",
                    )}
                    title={item.text} // Show full text on hover
                  >
                    {isMiddle && visibleItems.length > 2 ? "..." : item.text}
                  </BaseLink>
                ) : (
                  <span
                    className={cn(
                      "truncate text-xs font-medium text-gray-700 dark:text-gray-300",
                      isRTLMode ? "mr-1" : "ml-1",
                      // On mobile, limit width for better display
                      "max-w-24",
                    )}
                    title={item.text} // Show full text on hover
                  >
                    {item.text}
                  </span>
                )}
              </li>
            );
          })}
        </div>

        {/* Desktop view - all items */}
        <div className="hidden w-full items-center md:flex">
          {items.map((item, index) => (
            <li key={`desktop-${index}`} className="flex items-center">
              {index > 0 && (
                <>
                  <ChevronLeftIcon
                    className={cn("h-5 w-5 text-gray-400 dark:text-gray-500 ltr:hidden")}
                    aria-hidden="true"
                  />
                  <ChevronRightIcon
                    className={cn("h-5 w-5 text-gray-400 dark:text-gray-500 rtl:hidden")}
                    aria-hidden="true"
                  />
                </>
              )}
              {item.url ? (
                <BaseLink
                  to={item.url}
                  className={cn(
                    "ml-2 text-sm font-medium rtl:mr-2",
                    index === items.length - 1
                      ? "text-gray-700 dark:text-gray-300"
                      : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300",
                  )}
                >
                  {item.text}
                </BaseLink>
              ) : (
                <span
                  className={cn(
                    "text-sm font-medium text-gray-700 dark:text-gray-300",
                    isRTLMode ? "mr-2" : "ml-2",
                  )}
                >
                  {item.text}
                </span>
              )}
            </li>
          ))}
        </div>
      </ol>
    </nav>
  );
}
